import { WorkItem } from '../components/workitems/workItemTypes';
import { AppointmentType } from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface CreateAppointmentDto {
  title: string;
  type: AppointmentType;
  startAt: string;
  endAt: string;
  assignedUserFullName: string;
  customerId?: string;
  customerName?: string;
  notes?: string;
}

export interface UpdateAppointmentDto {
  title?: string;
  type?: AppointmentType;
  startAt?: string;
  endAt?: string;
  assignedUserFullName?: string;
  customerId?: string;
  customerName?: string;
  notes?: string;
  status?: string;
}

/**
 * Fetch appointments within a date range for the calendar view.
 */
export async function getAppointments(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<WorkItem[]> {
  const params = new URLSearchParams({ from, to });
  const response = await fetch(`${API_BASE_URL}/appointments?${params}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch appointments: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  const items = Array.isArray(data) ? data : (data.items ?? []);
  return items as WorkItem[];
}

/**
 * Create a standalone appointment (work item of type 'appointment').
 */
export async function createAppointment(
  data: CreateAppointmentDto,
  signal?: AbortSignal,
): Promise<WorkItem> {
  const response = await fetch(`${API_BASE_URL}/appointments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to create appointment: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update an existing appointment.
 */
export async function updateAppointment(
  guid: string,
  data: UpdateAppointmentDto,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/appointments/${guid}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update appointment: ${response.status} ${response.statusText}`);
  }
}

/**
 * Delete/cancel an appointment.
 */
export async function deleteAppointment(guid: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/appointments/${guid}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete appointment: ${response.status} ${response.statusText}`);
  }
}

/**
 * Link a standalone appointment to an existing job work item.
 */
export async function linkAppointmentToJob(
  appointmentGuid: string,
  jobGuid: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/appointments/${appointmentGuid}/link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobGuid }),
  });

  if (!response.ok) {
    throw new Error(`Failed to link appointment to job: ${response.status} ${response.statusText}`);
  }
}
